import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Info, Search, ShieldCheck, Star } from "lucide-react";
import { BIZ, TEAM, HOW_WE_ASSIST, REPUTATION } from "./espirituData";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import LawInquiryForm from "@/components/LawInquiryForm";

const GREEN = "#1B4332";
const AMBER = "#D4A017";
const CREAM = "#FAF6EC";
const INK = "#242019";
const SLATE = "#5B5748";

const fadeUp = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-40px" }, transition: { duration: 0.45, ease: "easeOut" } };

const initialsOf = (name) => name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("");

// Sidebar layout: office info stays fixed on the left, the team roster leads the content on
// the right — putting "who's actually here" first, since Espiritu's strength is a real working
// team rather than a single named founder.
export default function EspirituLawSite() {
  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Inter', sans-serif" }} data-testid="espiritu-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>

      <div data-testid="law-disclaimer" className="bg-black text-white text-center text-xs py-2.5 px-4">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: AMBER }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <section className="py-14 sm:py-20" style={{ background: GREEN }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_.78fr] gap-10 lg:gap-20 items-end">
          <motion.div {...fadeUp}>
            <p className="text-xs font-bold uppercase tracking-[.2em]" style={{ color: "#E8CB6E" }}>Bacolod City · Legal team</p>
            <h1 className="mt-5 text-4xl sm:text-6xl font-extrabold text-white leading-[1.02]">{BIZ.tagline}</h1>
            <p className="mt-6 max-w-2xl text-white/70 leading-7">A working office of associate attorneys, paralegals and researchers — not a single anonymous practitioner.</p>
            <a href="#consultation" data-testid="espiritu-hero-cta" className="mt-8 inline-flex min-h-11 items-center px-6 text-sm font-semibold" style={{ background: AMBER, color: GREEN }}>Request Consultation</a>
          </motion.div>
          <aside className="border-t border-white/20 pt-6 text-white">
            <div className="flex items-end justify-between gap-6"><div><p className="text-xs uppercase tracking-[.18em] text-white/50">Public reputation</p><strong className="mt-2 block text-4xl">{REPUTATION.rating}★</strong></div><p className="max-w-44 text-right text-xs text-white/55">{REPUTATION.reviewCountLabel}</p></div>
            <p className="mt-6 flex items-start gap-3 text-sm text-white/75"><MapPin size={17} style={{ color: AMBER }} className="mt-0.5 shrink-0" />{BIZ.address}</p>
          </aside>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 lg:py-16 grid lg:grid-cols-[280px_1fr] gap-10">
        {/* SIDEBAR */}
        <motion.aside {...fadeUp} className="lg:sticky lg:top-8 lg:self-start rounded-2xl p-7 text-white" style={{ background: GREEN }}>
          <h2 className="text-lg font-bold leading-snug">Bacolod Office</h2>
          <p className="mt-1.5 text-xs" style={{ color: "#E8CB6E" }}>Bacolod City, Negros Occidental</p>
          <p className="mt-5 text-sm text-white/75 leading-relaxed">A working office of associate attorneys, paralegals and researchers — not a single anonymous practitioner.</p>
          <div className="mt-6 space-y-3 text-sm text-white/85">
            <p className="flex items-start gap-2.5"><MapPin size={16} style={{ color: AMBER }} className="mt-0.5 shrink-0" />{BIZ.address}</p>
            <p className="flex items-start gap-2.5"><Phone size={16} style={{ color: AMBER }} className="mt-0.5 shrink-0" /><a href={`tel:${BIZ.phone}`} data-testid="espiritu-phone-link" className="hover:underline">{BIZ.phoneDisplay}</a></p>
          </div>
          <a href="#consultation" className="mt-7 flex min-h-11 items-center justify-center px-6 text-sm font-semibold" style={{ background: AMBER, color: GREEN }}>Request Consultation</a>
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <div className="inline-flex items-center gap-1">{[...Array(5)].map((_, i) => <Star key={i} size={13} fill={AMBER} style={{ color: AMBER }} />)}</div>
            <p className="mt-1.5 text-xs text-white/60">{REPUTATION.rating}★ · {REPUTATION.reviewCountLabel}</p>
          </div>
        </motion.aside>

        {/* MAIN — practice support leads; useful directory detail follows. */}
        <main>
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-bold mb-8" style={{ color: GREEN }}>How We Assist</motion.h2>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            {HOW_WE_ASSIST.map((s) => (
              <motion.div key={s.title} {...fadeUp} className="bg-white border-t-4 p-5 shadow-sm" style={{ borderColor: AMBER }}>
                <Search size={18} style={{ color: AMBER }} />
                <p className="mt-2 font-semibold text-sm" style={{ color: GREEN }}>{s.title}</p>
                <p className="mt-1 text-xs" style={{ color: SLATE }}>{s.text}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="flex items-start gap-2.5 p-4 text-xs bg-white mb-14">
            <ShieldCheck size={16} className="shrink-0 mt-0.5" style={{ color: AMBER }} />
            A formal, current list of practice areas was not publicly available — reach out directly to discuss whether your matter is a fit.
          </motion.div>

          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: GREEN }}>Our Team</motion.h2>
          <motion.p {...fadeUp} className="text-sm mb-8" style={{ color: SLATE }}>Current, confirmed team members — we've operated with associate attorneys, paralegals and researchers over the years.</motion.p>
          <div className="grid sm:grid-cols-2 gap-5 mb-14">
            {TEAM.map((t) => (
              <motion.div key={t.name} {...fadeUp} className="bg-white rounded-lg p-5 shadow-sm flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: GREEN }}>{initialsOf(t.name)}</span>
                <div>
                  <p className="font-bold text-sm" style={{ color: GREEN }}>{t.name}</p>
                  <p className="mt-0.5 text-xs" style={{ color: SLATE }}>{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="rounded-lg overflow-hidden shadow-sm mb-8" style={{ minHeight: 220 }}>
            <iframe title="Espiritu and Associates map" src={BIZ.mapEmbed} className="w-full h-full" style={{ border: 0, minHeight: 220 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>

          <div id="consultation">
            <LawInquiryForm
              testidPrefix="espiritu"
              businessName={BIZ.name}
              accent={GREEN}
              accentText="#FFFFFF"
              phone={BIZ.phone}
              phoneDisplay={BIZ.phoneDisplay}
            />
          </div>
          <p className="mt-2 text-xs" style={{ color: SLATE }}>{REPUTATION.note}</p>
        </main>
      </div>

      <footer className="py-8" style={{ background: GREEN }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/60">
          <p className="font-bold text-white">Espiritu and Associates Law Office</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm current details before visiting.</p>
        </div>
      </footer>

      <ConceptCTAPill slug="espiritu-associates-law-bacolod-concept" />
    </div>
  );
}
